import { Button, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper, Stack } from "@mui/material";
import { IMenuTableAction, RowData } from "../types/tableProps";
import { useEffect, useRef, useState } from "react";

interface ITableActionsList {
    actions: IMenuTableAction[];
    rowData: RowData;
    icon: string;
    title: string;
}

export function TableActionsList({ actions, rowData, title, icon }: ITableActionsList) {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLButtonElement | null>(null);
    const disabled = actions.filter(act => act.showAction).length === 0;

    const handleClose = (event: MouseEvent | TouchEvent) => {
        if (
            anchorRef.current &&
            anchorRef.current.contains(event.target as Node)
        ) {
            return;
        }
        setOpen(false);
    };

    const handleListKeyDown = (event: any) => {
        if (event.key === "Tab" || event.key === "Escape") {
            event.preventDefault();
            setOpen(false);
        }
    };

    const prevOpen = useRef(open);

    useEffect(() => {
        if (prevOpen.current === true && open === false && anchorRef.current) {
            anchorRef.current.focus();
        }
        prevOpen.current = open;
    }, [open]);

    const renderMenuItem = () => {
        if (actions.length < 0) return null;

        return (
            actions
                .filter(act => act.showAction?.(rowData))
                .sort((a, b) => a.title.localeCompare(b.title))
                .map((act) => (
                    <MenuItem
                        key={act.title}
                        disabled={disabled}
                        onClick={() => {
                            act.act(rowData);
                            setOpen(false);
                        }}
                    >
                        {act.icon && (
                            <span
                                style={{
                                    color: act?.color || "var(--primary-color)",
                                }}
                                className="material-symbols-outlined"
                            >
                                {act.icon}
                            </span>
                        )}
                        <p
                            style={
                                act?.color ? { color: act?.color } : {
                                    color: "var(--primary-color)"
                                }}
                            className="ml-2"
                        >
                            {act.title}
                        </p>
                    </MenuItem>
                ))
        );
    };

    return (
        <Stack direction="row" spacing={2}>
            <div>
                <Button
                    ref={anchorRef}
                    id="composition-button"
                    aria-controls={open ? "composition-menu" : undefined}
                    aria-expanded={open ? "true" : undefined}
                    aria-haspopup="true"
                    onClick={() => setOpen((prevOpen) => !prevOpen)}
                    className="!bg-transparent"
                >
                    {icon ? (
                        <span className="material-symbols-outlined icon dots text-[--primary-color]">{icon}</span>
                    ) : (
                        title
                    )}
                </Button>
                <Popper
                    open={open}
                    anchorEl={anchorRef.current}
                    role={undefined}
                    placement="bottom-start"
                    transition
                    className="z-10"
                >
                    {({ TransitionProps, placement }) => (
                        <Grow
                            {...TransitionProps}
                            style={{
                                transformOrigin:
                                    placement === "bottom-start" ? "left top" : "left bottom",
                            }}
                        >
                            <Paper>
                                <ClickAwayListener onClickAway={handleClose}>
                                    <MenuList
                                        autoFocusItem={open}
                                        id="composition-menu"
                                        aria-labelledby="composition-button"
                                        onKeyDown={handleListKeyDown}
                                    >
                                        {renderMenuItem()}
                                    </MenuList>
                                </ClickAwayListener>
                            </Paper>
                        </Grow>
                    )}
                </Popper>
            </div>
        </Stack>
    )
}